import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../componentLibrary/Button";
import Tutorial1 from "../assets/tutorial/Tutorial1Learn.svg";
import Tutorial2 from "../assets/tutorial/Tutorial2Apply.svg";
import Tutorial3 from "../assets/tutorial/Tutorial3Customize.svg";
import Tutorial4 from "../assets/tutorial/Tutorial4Use.svg";
import {
  useUpdateTutorialViewed,
  useUpdateTutorialVideoWatched,
} from "../api/mutations";
import { TUTORIAL_CONFIG } from "../utils/tutorial";

interface TutorialPage {
  title?: string | React.ReactNode;
  description?: string;
  image?: string;
  video?: string;
}

const tutorialPages: TutorialPage[] = [
  {
    title: (
      <>
        How to Use Psych for Life<sup>®</sup>
      </>
    ),
    description:
      "Please take just a few minutes to watch the video so you can get the most out of your experience.",
    video:
      "https://player.vimeo.com/video/1149514683?h=b4429de07a&badge=0&autopause=0&player_id=0&app_id=58479",
  },
  {
    title: "Learn Quickly",
    description:
      "Short engaging videos and text presented simply and clearly as tools you can use.",
    image: Tutorial1,
  },
  {
    title: "Apply What You've Learned",
    description:
      "Simple activities designed to help you practice using these tools.",
    image: Tutorial2,
  },
  {
    title: "Select and Customize",
    description:
      "You're an individual, your own person. Collect and personalize the tools that work best for you to create your own toolbox.",
    image: Tutorial3,
  },
  {
    title: "Use!",
    description: "Put your tools into action at home, work – wherever you are.",
    image: Tutorial4,
  },
];

const TutorialPage = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [videoButtonClicked, setVideoButtonClicked] = useState(false);
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const videoIframeRef = useRef<HTMLIFrameElement>(null);
  const updateTutorialViewed = useUpdateTutorialViewed();
  const updateTutorialVideoWatched = useUpdateTutorialVideoWatched();
  const videoWatchTrackedRef = useRef(false);
  const vimeoPlayTrackedRef = useRef(false);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const scrollPosition = slider.scrollLeft;
      const cardWidth = slider.offsetWidth;
      const index = Math.round(scrollPosition / cardWidth);
      setCurrentPage(index);
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Reset video button state when navigating away from page 0
    if (currentPage !== 0) {
      setVideoButtonClicked(false);
      videoWatchTrackedRef.current = false;
      vimeoPlayTrackedRef.current = false;
    }
  }, [currentPage]);

  // Get video URL with API parameter for Vimeo postMessage support
  const getVideoUrlWithTracking = (videoUrl: string) => {
    try {
      const url = new URL(videoUrl);
      // Add API parameter for Vimeo to enable postMessage events
      if (url.hostname.includes("vimeo.com")) {
        if (!url.searchParams.has("api")) {
          url.searchParams.set("api", "1");
        }
      }
      return url.toString();
    } catch {
      return videoUrl;
    }
  };

  // Listen for Vimeo postMessage events to track when video actually plays
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check that the message came from this component's iframe
      if (event.source !== videoIframeRef.current?.contentWindow) {
        return;
      }

      // Vimeo sends play events via postMessage
      if (event.data && typeof event.data === "string") {
        try {
          const data = JSON.parse(event.data);
          if (data.event === "play" && !vimeoPlayTrackedRef.current) {
            vimeoPlayTrackedRef.current = true;
            updateTutorialVideoWatched.mutate();
          }
        } catch {
          // Not a JSON message, ignore
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Setup Vimeo event listeners if we're on the video page
    const setupVimeoListener = () => {
      const iframe = videoIframeRef.current;
      if (iframe && iframe.contentWindow && currentPage === 0) {
        const videoUrl = tutorialPages[0].video;
        if (videoUrl && videoUrl.includes("vimeo.com")) {
          const playMessage = JSON.stringify({
            method: "addEventListener",
            value: "play",
          });
          iframe.contentWindow.postMessage(playMessage, "*");
        }
      }
    };

    // Setup listener after a short delay to ensure iframe is loaded
    const timeoutId = setTimeout(setupVimeoListener, 1000);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
    };
  }, [currentPage, updateTutorialVideoWatched]);

  const scrollToPage = (index: number) => {
    const slider = sliderRef.current;
    if (slider) {
      const cardWidth = slider.offsetWidth;
      slider.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setCurrentPage(index);
    }
  };

  const handleNext = () => {
    if (currentPage < tutorialPages.length - 1) {
      scrollToPage(currentPage + 1);
    } else {
      completeTutorial();
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      scrollToPage(currentPage - 1);
    } else {
      navigate("/tutorial");
    }
  };

  const handleSkip = () => {
    if (currentPage === 0) {
      scrollToPage(1);
    } else {
      completeTutorial();
    }
  };

  const completeTutorial = () => {
    updateTutorialViewed.mutate(TUTORIAL_CONFIG.CURRENT_VERSION, {
      onSuccess: () => {
        navigate("/explore");
      },
      onError: () => {
        // Still navigate even if marking as viewed fails
        navigate("/explore");
      },
    });
  };

  const handleExploreTopics = () => {
    completeTutorial();
  };

  const handlePlayVideo = () => {
    const iframe = videoIframeRef.current;
    if (iframe && iframe.contentWindow) {
      const playMessage = JSON.stringify({
        method: "play",
      });
      iframe.contentWindow.postMessage(playMessage, "https://player.vimeo.com");
    }
    setVideoButtonClicked(true);
    // Track video watch when "Watch the video" button is clicked
    if (!videoWatchTrackedRef.current) {
      videoWatchTrackedRef.current = true;
      updateTutorialVideoWatched.mutate();
    }
  };

  const handleVideoButtonClick = () => {
    if (!videoButtonClicked) {
      handlePlayVideo();
    } else {
      handleNext();
    }
  };

  return (
    <div className="flex justify-center w-full bg-white">
      <div className="w-full max-w-2xl mx-auto flex flex-col">
        <div
          ref={sliderRef}
          className="flex-1 overflow-x-auto scroll whitespace-nowrap scroll-smooth no-scrollbar"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {tutorialPages.map((page, index) => (
            <div
              key={index}
              className="inline-flex w-full flex-shrink-0"
              style={{ scrollSnapAlign: "start" }}
            >
              <div
                className={`flex flex-col items-center justify-start w-full p-4 pb-2 whitespace-normal ${
                  index === 0 ? "pt-0" : ""
                }`}
              >
                {page.video && currentPage === index ? (
                  <div className="w-full max-w-4xl aspect-video mt-0">
                    <iframe
                      ref={videoIframeRef}
                      src={getVideoUrlWithTracking(page.video)}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      title={
                        typeof page.title === "string" ? page.title : "Video"
                      }
                      className="w-full h-full rounded-lg"
                    />
                  </div>
                ) : page.video && currentPage !== index ? (
                  <div className="w-full max-w-4xl aspect-video mt-0" />
                ) : page.image ? (
                  <div>
                    <img
                      src={page.image}
                      alt={typeof page.title === "string" ? page.title : ""}
                      className="w-full max-w-md h-auto object-contain"
                    />
                  </div>
                ) : null}
                <h1 className="text-xl font-semibold mt-3 px-4 text-center">
                  {page.title}
                </h1>
                <p className="mt-2 text-gray-700 px-8 text-sm md:text-base text-center">
                  {page.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center w-full px-4 pb-4">
          {currentPage === 0 ? (
            <div className="flex flex-col justify-center space-y-2">
              <Button className="w-48 md:w-64" onClick={handleVideoButtonClick}>
                {videoButtonClicked ? "Next" : "Watch the video"}
              </Button>
            </div>
          ) : currentPage < tutorialPages.length - 1 ? (
            <div className="flex justify-center items-center space-x-4 w-full">
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>

              <Button onClick={handleNext} variant="primary">
                Next
              </Button>
            </div>
          ) : (
            <div className="flex justify-center items-center space-x-4 w-full">
              <Button onClick={handleBack} variant="secondary">
                Back
              </Button>

              <Button onClick={handleExploreTopics} variant="primary">
                Begin exploring
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-col justify-center items-center py-2">
          {currentPage < tutorialPages.length - 1 && (
            <Button
              onClick={handleSkip}
              variant="alternative"
              className="text-gray-900 mt-2"
            >
              Skip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialPage;
